import BaseHttpService from './BaseHttpService';
import BlainBaseService from './BlainBaseService';
import EventBusService from './EventBusService';
import LoggerService from './LoggerService';
import { CartUpdateResolutionType, ICartAddProductResponse, ICartGiftCardUpdateResponse, ICartGiftCardValues, ICartProductUpdateResponse, ICartProductValues, ICartUpdateResponse, ICartZipcodeUpdateResponse, IUpdateFulfillmentOptionRequest, IZipcodeValidationResponse } from '../types/Order';
import { EventBusEvents, IEventBusService } from '../types/EventBus';
import { IBaseHttpService } from '../types/Http';
import { IGiftCardValues } from '../types/GiftCard';
import { ILoggerService } from '../types/Logger';
import { IQuickCartSummaryJson } from "../types/Order";

interface IOrderProducts {
	blainNumber: string;
	qty: number;
	unitPrice: string;
	vendorBrand: string;
	productName: string;
	imageUrl: string;
	productUrl: string;
}

interface GTMCartEvent {
	orderId: number;
	blainNumber: string;
	vendorBrand: string;
	productName: string;
	deliveryChannel: string;
	totalValue?: number;
	qty?: number;
	orderProducts: IOrderProducts[],
	orderSubtotal: number,
	event: string;
}

export default class CartService extends BlainBaseService {

	constructor(
		httpSvc: IBaseHttpService = new BaseHttpService(),
		eventBusService: IEventBusService = new EventBusService(),
		logger: ILoggerService = new LoggerService()
	) {
		super(eventBusService, logger, httpSvc);
	}

	_getOrderProducts(cartSummary: IQuickCartSummaryJson): IOrderProducts[] {
		return cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));
	}

	_sendRemoveProductToGTM(blainNumber: string, totalValue: number, orderProducts: IOrderProducts[], orderSubtotal: number, orderId: number) {
		// Remove from Cart event for Google Tag Manager
		const cartEvent: GTMCartEvent = {
			blainNumber: blainNumber,
			vendorBrand: null,
			productName: null,
			deliveryChannel: null,
			totalValue: totalValue,
			orderProducts: orderProducts,
			orderSubtotal: orderSubtotal,
			orderId: orderId,
			event: "cart_ajax_remove"
		};
		window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
		window.blainGtmDataLayer.push(cartEvent);
	}

	async addProduct(productValues: ICartProductValues): Promise<ICartAddProductResponse> {
		try {
			const response = await this._httpSvc.post<ICartAddProductResponse>('/cart/a/add-product', productValues);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when adding product #${productValues.blainNumber}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);

				const thisProduct = response.cartSummary.productsInCart.find(cartProduct => cartProduct.blainNumber === productValues.blainNumber);

				const orderProducts = this._getOrderProducts(response.cartSummary);

				// Add to Cart event for Google Tag Manager
				const cartEvent: GTMCartEvent = {
					blainNumber: thisProduct.blainNumber,
					vendorBrand: thisProduct.vendorBrand,
					productName: thisProduct.productName,
					deliveryChannel: productValues.fulfillmentType,
					totalValue: thisProduct.unitPrice,
					qty: thisProduct.qty,
					orderProducts: orderProducts,
					orderSubtotal: response.cartSummary.subtotal,
					orderId: response.orderJson.orderId,
					event: "cart_ajax_add"
				};
				window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
				window.blainGtmDataLayer.push(cartEvent);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async removeProduct(blainNumber: string, totalValue: number): Promise<ICartUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartUpdateResponse>('/cart/a/remove-product', { blainNumber });
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when removing product #${blainNumber}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.CartDeleted) {
				this._sendRemoveProductToGTM(blainNumber, totalValue, [], 0, response.orderId);

				this._eventBus.emit(EventBusEvents.UpdateQuickCart, {} as IQuickCartSummaryJson);
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);

				const orderProducts = this._getOrderProducts(response.cartSummary);

				this._sendRemoveProductToGTM(blainNumber, totalValue, orderProducts, response.cartSummary.subtotal, response.orderJson.orderId);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateProduct(productValues: ICartProductValues): Promise<ICartProductUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartProductUpdateResponse>('/cart/a/update-product', productValues);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when updating product #${productValues.blainNumber}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);

				const orderProducts = this._getOrderProducts(response.cartSummary);

				const cartProduct = orderProducts.find(product => product.blainNumber === productValues.blainNumber);

				// Add to Cart event for Google Tag Manager
				const cartEvent: GTMCartEvent = {
					blainNumber: productValues.blainNumber,
					vendorBrand: cartProduct.vendorBrand,
					productName: cartProduct.productName,
					deliveryChannel: productValues.fulfillmentType,
					qty: productValues.itemQty,
					orderProducts: orderProducts,
					orderSubtotal: response.cartSummary.subtotal,
					orderId: response.orderJson.orderId,
					event: "cart_ajax_update"
				};
				window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
				window.blainGtmDataLayer.push(cartEvent);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateShipToZip(shipToZip: string): Promise<ICartZipcodeUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartZipcodeUpdateResponse>('/cart/a/update-ship-to-zip', { shipToZip });
			if (response.cartSummary) this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async validateShipToZip(shipToZip: string): Promise<IZipcodeValidationResponse> {
		try {
			const response = await this._httpSvc.post<IZipcodeValidationResponse>('/cart/a/validate-ship-to-zip', { shipToZip });
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when updating ZIP code ${shipToZip}`;
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async addGiftCard(giftCardValues: IGiftCardValues): Promise<ICartGiftCardUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartGiftCardUpdateResponse>('/cart/a/add-giftcard', giftCardValues);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when adding gift card`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateGiftCard(giftCardValues: ICartGiftCardValues): Promise<ICartGiftCardUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartGiftCardUpdateResponse>('/cart/a/update-giftcard', giftCardValues);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when updating gift card with id ${giftCardValues.itemID}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async removeGiftCard(giftCardValues: ICartGiftCardValues): Promise<ICartUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartUpdateResponse>('/cart/a/remove-giftcard', giftCardValues);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when removing gift card with id ${giftCardValues.itemID}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateTaxExempt(avalaraCustomerId: number): Promise<ICartUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartUpdateResponse>('/cart/a/update-tax-exempt', { avalaraCustomerID: avalaraCustomerId });
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when selecting tax exempt profile id ${avalaraCustomerId}`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateFulfillmentOptionForProducts(payload: IUpdateFulfillmentOptionRequest): Promise<ICartUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartUpdateResponse>('/cart/a/update-all-fulfillment-options', payload);
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when trying to update fulfillment option for products`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

	async updateShippingMethod(shippingMethod: string): Promise<ICartUpdateResponse> {
		try {
			const response = await this._httpSvc.post<ICartUpdateResponse>('/cart/a/update-shipping-method', { shippingMethod });
			if (response.resolutionType === CartUpdateResolutionType.CartNotFound) {
				throw `Cart not found when trying to update fulfillment option for products`;
			}
			if (response.resolutionType === CartUpdateResolutionType.Success) {
				this._eventBus.emit(EventBusEvents.UpdateQuickCart, response.cartSummary);
			}
			return response;
		} catch (err) {
			this.handleError(err);
		}
	}

}